import { FC, Suspense, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Button, Dialog, DialogActions, DialogContent, Skeleton, Stack, Typography } from '@mui/material'

import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import GRDialogTitle from '../../../components/GRDialogTitle/GRDialogTitle'
import {
  AnalysisNote,
  gameNoteLegacyId,
  useDeleteAnalysisNoteMutation,
  useLazyGetAnalysisNoteQuery,
  useSaveAnalysisNoteMutation,
} from '../../api/analysis-notes'
import { getValidationErrorMessage } from '../../helpers/apiValidationError'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'
import { LazyHTMLEditor } from '../HTMLEditor/LazyHTMLEditor'

const deleteAction = 'delete'

// Legacy's editor requires content; an empty paragraph doesn't count.
export const hasNoteContent = (html: string) =>
  /<(img|iframe|video)\b/i.test(html) ||
  html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim() !== ''

type AnalysisNoteEditDialogProps = {
  open: boolean
  // A note without an `id` is created.
  note?: AnalysisNote
  // Names the feature in the title and messages; the game's own note has none.
  featureName?: string
  // Shown under the title, e.g. when the note is another game's.
  gameName?: string
  canDelete?: boolean
  onClose: () => void
}

/**
 * Adds, edits and deletes an analysis note (`analyses/notes`) of a feature or, with `featureLegacyId` 0, of the game.
 * Each write is read back and a note stored differently is reported.
 */
export const AnalysisNoteEditDialog: FC<AnalysisNoteEditDialogProps> = ({ open, note, featureName, gameName, canDelete = true, onClose }) => {
  const { t } = useTranslation()
  const { run, notifySuccess, notifyError } = useMutationFeedback()
  const [saveNote, saveState] = useSaveAnalysisNoteMutation()
  const [deleteNote, deleteState] = useDeleteAnalysisNoteMutation()
  const [readNote] = useLazyGetAnalysisNoteQuery()
  // TinyMCE normalises the stored HTML, so "unchanged" is compared with what the editor holds right after it loads.
  const [baseline, setBaseline] = useState<string>()
  const [content, setContent] = useState('')
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)
  // A note created by a save whose read-back differed; saving again updates it instead of creating another.
  const [createdNote, setCreatedNote] = useState<AnalysisNote>()
  const editedNote = note?.id ? note : createdNote ?? note
  const busy = saveState.isLoading || deleteState.isLoading
  const isGameNote = note?.featureLegacyId === gameNoteLegacyId

  useEffect(() => {
    if (open) {
      setBaseline(undefined)
      setContent(note?.notes ?? '')
      setCreatedNote(undefined)
    }
  }, [open, note])

  const noteMessage = (key: string, options: Record<string, string> = {}) =>
    isGameNote ? t(`internal-analysts:analysis_notes_game_${key}`, options) : t(`internal-analysts:analysis_notes_${key}`, { name: featureName, ...options })

  const handleSave = async () => {
    if (!note || !editedNote) return
    const body = { ...editedNote, notes: content }
    try {
      const { stored } = await saveNote(body).unwrap()
      if (!editedNote.id && stored?.id) setCreatedNote(stored)
      if (!stored || stored.notes !== content || (editedNote.id && stored.id !== editedNote.id)) {
        notifyError(noteMessage('not_stored'))
        return
      }
      notifySuccess(noteMessage(note.id ? 'edited' : 'added'))
      onClose()
    } catch (error) {
      // A failed create may still have stored the note (e.g. only its read-back failed); adopting it makes a retry update it.
      if (!editedNote.id) {
        try {
          const stored = await readNote({ gameId: editedNote.gameId, featureLegacyId: editedNote.featureLegacyId }).unwrap()
          if (stored?.id) setCreatedNote(stored)
        } catch {}
      }
      const reason = getValidationErrorMessage(error)
      notifyError(reason ? noteMessage('save_failed_reason', { reason }) : noteMessage('save_failed'))
    }
  }

  const handleDelete = async () => {
    setIsConfirmingDelete(false)
    if (!editedNote?.id) return
    const deleted = await run(deleteNote({ ...editedNote, id: editedNote.id }).unwrap(), { error: noteMessage('delete_failed') })
    if (!deleted.ok) return
    if (deleted.result.stored) {
      notifyError(noteMessage('not_deleted'))
      return
    }
    notifySuccess(noteMessage('deleted'))
    onClose()
  }

  const changed = baseline !== undefined && content !== baseline

  return (
    <>
      <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
        <GRDialogTitle onClose={busy ? undefined : onClose}>
          <Box component="span">
            {isGameNote ? t('internal-analysts:analysis_notes_game_edit_title') : t('internal-analysts:analysis_notes_edit_title', { name: featureName })}
            {gameName && (
              <Typography component="span" display="block" variant="body2" color="text.secondary">
                {gameName}
              </Typography>
            )}
          </Box>
        </GRDialogTitle>
        <DialogContent>
          {open && (
            <Suspense fallback={<Skeleton variant="rectangular" height={240} />}>
              <LazyHTMLEditor
                initialValue={note?.notes ?? ''}
                onInit={(_event, editor) => {
                  const loaded = editor.getContent()
                  setBaseline(loaded)
                  setContent(loaded)
                }}
                onEditorChange={(value) => setContent(value)}
                disabled={busy}
                init={{
                  menubar: false,
                  plugins: ['lists', 'link', 'media', 'autoresize'],
                  toolbar: 'undo redo | blocks blockquote | bold italic underline | bullist numlist | link media',
                  block_formats: 'Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3; Heading 4=h4; Heading 5=h5; Heading 6=h6',
                  placeholder: t('internal-analysts:analysis_notes_placeholder'),
                  min_height: 240,
                  convert_urls: false,
                  // Legacy's editor stored notes unindented and without named entities; customers read them as stored.
                  indent: false,
                  entity_encoding: 'raw',
                }}
              />
            </Suspense>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          {canDelete ? (
            <Button color="error" onClick={() => setIsConfirmingDelete(true)} disabled={!editedNote?.id || busy}>
              {t('internal-common:delete')}
            </Button>
          ) : (
            <Box />
          )}
          <Stack direction="row" spacing={1}>
            <Button onClick={onClose} disabled={busy}>
              {t('internal-common:cancel')}
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={!changed || !hasNoteContent(content) || busy}>
              {t('internal-common:save')}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={isConfirmingDelete}
        confirmDialogData={{
          title: t('internal-analysts:analysis_notes_delete_title'),
          confirmText: t('internal-analysts:analysis_notes_delete_confirm'),
          actionText: t('internal-common:delete'),
          destructiveAction: true,
          data: deleteAction,
        }}
        onClose={(confirmed) => (confirmed?.data === deleteAction ? handleDelete() : setIsConfirmingDelete(false))}
      />
    </>
  )
}
